// The legacy shuffle, bit for bit (deck.js:566-603). The pattern is a wire format: it is sent with
// every win (`shuffle_pattern`), stored on the server, replayed by "Retry game", sent by the
// multiplayer server, and built by the e2e setup hook. See .ai/rewrite-notes.md 2.10.

/**
 * "Swap with any position" shuffle (not Fisher-Yates): for i in 0..len-1, p = pattern[i] (replay)
 * or floor(rng() * len) (new deal, pushed to the pattern), swap positions i and p.
 * Returns the shuffled positions (indexes into the base deck) and the pattern used.
 */
export function shuffle(len: number, pattern: readonly number[] | null, rng: () => number): { order: number[]; pattern: number[] } {
  const out = Array.from({ length: len }, (_, i) => i);
  const pat: number[] = [];
  for (let i = 0; i < len; i++) {
    const p = pattern ? pattern[i]! : Math.floor(rng() * len);
    pat.push(p);
    const t = out[i]!;
    out[i] = out[p]!;
    out[p] = t;
  }
  return { order: out, pattern: pat };
}

/** Is `pattern` a usable pattern for a deck of `len` cards? */
export function isValidPattern(pattern: readonly number[], len: number): boolean {
  return pattern.length === len && pattern.every((p) => Number.isInteger(p) && p >= 0 && p < len);
}

/**
 * The pattern that turns the base order into `wanted` (catalog KL-H02; the same algorithm as the e2e
 * driver's dealLayout): for each i, j = first position >= i holding wanted[i]; pattern[i] = j; swap.
 * `wanted` must be a permutation of `base` (duplicate ids allowed, as in 104-card decks).
 */
export function patternForOrder(base: readonly string[], wanted: readonly string[]): number[] {
  if (base.length !== wanted.length) throw new Error(`patternForOrder: ${wanted.length} cards for a deck of ${base.length}`);
  const cur = base.slice();
  const pattern: number[] = [];
  for (let i = 0; i < wanted.length; i++) {
    let j = i;
    while (j < cur.length && cur[j] !== wanted[i]) j++;
    if (j >= cur.length) throw new Error(`patternForOrder: cannot place ${wanted[i]} at ${i}`);
    pattern.push(j);
    const t = cur[i]!;
    cur[i] = cur[j]!;
    cur[j] = t;
  }
  return pattern;
}
